import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface TrackingSettings {
  global_meta_pixel_id: string;
  global_google_tag_id: string;
  enable_meta_pixel: boolean;
  enable_gtm: boolean;
}

declare global {
  interface Window {
    fbq?: any;
    dataLayer?: any[];
    gtag?: any;
  }
}

export const GlobalTracking = () => {
  const [trackingSettings, setTrackingSettings] = useState<TrackingSettings>({
    global_meta_pixel_id: '',
    global_google_tag_id: '',
    enable_meta_pixel: false,
    enable_gtm: false
  });

  useEffect(() => {
    loadTrackingSettings();
  }, []);

  const loadTrackingSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('content_management')
        .select('*')
        .in('content_key', ['global_meta_pixel_id', 'global_google_tag_id', 'enable_meta_pixel', 'enable_gtm'])
        .eq('is_active', true);

      if (error) {
        console.error('Error loading tracking settings:', error);
        return;
      }

      const settingsMap = data?.reduce((acc, item) => {
        acc[item.content_key] = item.content_value;
        return acc;
      }, {} as Record<string, string>) || {};

      const settings = {
        global_meta_pixel_id: settingsMap.global_meta_pixel_id || '',
        global_google_tag_id: settingsMap.global_google_tag_id || '',
        enable_meta_pixel: settingsMap.enable_meta_pixel !== 'false',
        enable_gtm: settingsMap.enable_gtm !== 'false'
      };

      setTrackingSettings(settings);

      // Initialize tracking scripts
      if (settings.enable_meta_pixel && settings.global_meta_pixel_id) {
        initializeMetaPixel(settings.global_meta_pixel_id);
      }

      if (settings.enable_gtm && settings.global_google_tag_id) {
        initializeGTM(settings.global_google_tag_id);
      }
    } catch (error) {
      console.error('Error loading tracking settings:', error);
    }
  };

  const initializeMetaPixel = (pixelId: string) => {
    // Check if already initialized
    if (window.fbq) return;

    // Meta Pixel initialization script
    const script = document.createElement('script');
    script.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${pixelId}');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(script);

    // Add noscript fallback
    const noscript = document.createElement('noscript');
    noscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1" />`;
    document.head.appendChild(noscript);
  };

  const initializeGTM = (containerId: string) => {
    // Check if already initialized
    if (window.dataLayer) return;

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];
    
    // GTM initialization script
    const script = document.createElement('script');
    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${containerId}');
    `;
    document.head.appendChild(script);

    // Add noscript fallback for body (this should be added to the body, but we'll add it to head for now)
    const noscript = document.createElement('noscript');
    noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${containerId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
    document.head.appendChild(noscript);
  };

  // Utility functions for tracking events
  const trackEvent = (eventName: string, parameters?: Record<string, any>) => {
    // Meta Pixel tracking
    if (trackingSettings.enable_meta_pixel && window.fbq) {
      window.fbq('track', eventName, parameters);
    }

    // GTM tracking
    if (trackingSettings.enable_gtm && window.dataLayer) {
      window.dataLayer.push({
        event: eventName,
        ...parameters
      });
    }
  };

  // Export tracking functions to window for global access
  useEffect(() => {
    (window as any).cruzaSmartTrack = trackEvent;
  }, [trackingSettings]);

  return null; // This component doesn't render anything
};

// Utility function to track events from anywhere in the app
export const trackCruzaSmartEvent = (eventName: string, parameters?: Record<string, any>) => {
  if ((window as any).cruzaSmartTrack) {
    (window as any).cruzaSmartTrack(eventName, parameters);
  }
};